import React, { useState, useEffect, useMemo } from "react";
import { Card, CardBody, Modal, ModalHeader, ModalBody } from "reactstrap";
import ReactApexChart from "react-apexcharts";
import secureLocalStorage from "react-secure-storage";
import { fetchNodeData } from "utils/common-fetchData";
import Loader from "pages/Separate/Loader";

const MOD_PURPLE = "#752a97";

const SHORT_MONTH = [
  "Jan",
  "Feb",
  "Mar",
  "Apr",
  "May",
  "Jun",
  "Jul",
  "Aug",
  "Sep",
  "Oct",
  "Nov",
  "Dec",
];

// "2025-04" → "Apr"
const shortFromKey = (key) => {
  const mn = parseInt(key.split("-")[1], 10);
  return SHORT_MONTH[mn - 1] || key;
};

const getCurrentFY = () => {
  const now = new Date();
  const m = now.getMonth() + 1;
  const y = now.getFullYear();
  const startYear = m >= 4 ? y : y - 1;
  return {
    startYear,
    endYear: startYear + 1,
    label: `FY ${startYear}-${String(startYear + 1).slice(-2)}`,
  };
};

const isMonthIncomplete = (monthKey) => {
  const [yr, mn] = monthKey.split("-").map(Number);
  const now = new Date();
  return (
    yr > now.getFullYear() ||
    (yr === now.getFullYear() && mn > now.getMonth() + 1)
  );
};

const fmtL = (n) => {
  const abs = Math.abs(n);
  if (abs >= 100000) return `₹${(abs / 100000).toFixed(2)}L`;
  if (abs >= 1000) return `₹${(abs / 1000).toFixed(1)}K`;
  return `₹${abs}`;
};

const fmt = (n) =>
  Number(n || 0).toLocaleString("en-IN", { maximumFractionDigits: 2 });

const typeBadge = (type) => {
  if (!type) return null;
  const t = type.toLowerCase();
  if (t === "vendor")
    return (
      <span
        className="badge"
        style={{
          backgroundColor: "#e8ddf0",
          color: MOD_PURPLE,
          fontSize: "11px",
        }}
      >
        Vendor
      </span>
    );
  if (t === "budget")
    return (
      <span
        className="badge"
        style={{
          backgroundColor: "#d9ede2",
          color: "#2e7d4f",
          fontSize: "11px",
        }}
      >
        Budget
      </span>
    );
  return (
    <span
      className="badge"
      style={{ backgroundColor: "#f0dede", color: "#7a3333", fontSize: "11px" }}
    >
      Actual
    </span>
  );
};

const FinanceReportElement = () => {
  const [months, setMonths] = useState([]);
  const [isLoading, setLoading] = useState(false);
  const [activeIdx, setActiveIdx] = useState(null);
  const token = secureLocalStorage.getItem("auth_token");
  const { startYear, endYear, label } = getCurrentFY();

  useEffect(() => {
    fetchNodeData(
      setLoading,
      `financial_report?start_month=4&start_year=${startYear}&end_month=3&end_year=${endYear}`,
      token,
    ).then((res) => {
      if (!res?.months) return;
      setMonths(
        res.months.map((m) => ({
          month: m.month_label,
          month_key: m.month_key,
          income: {
            total: Number(m.income?.total || 0),
            items: (m.income?.transactions || []).map((t) => ({
              invoice_number: t.invoice_number,
              customer_name: t.customer_name,
              amount: t.converted_amount,
              date: t.receive_date,
              payment_mode: t.payment_mode,
            })),
          },
          expense: {
            total: Number(m.expense?.total || 0),
            items: (m.expense?.entries || []).map((e) => ({
              desc: e.title || "—",
              amount: e.amount,
              date: e.date,
              type: e.type,
            })),
          },
        })),
      );
    });
  }, []);

  const ledger = useMemo(() => {
    let bf = 0;
    return months.map((m) => {
      const net = m.income.total - m.expense.total;
      const closing = bf + net;
      const row = { ...m, net, bf, closing };
      bf = closing;
      return row;
    });
  }, [months]);

  // Chart only renders completed months — no empty future bars
  const completedRows = ledger.filter((r) => !isMonthIncomplete(r.month_key));

  const totalIncome = ledger.reduce((s, r) => s + r.income.total, 0);
  const totalExpense = ledger.reduce((s, r) => s + r.expense.total, 0);
  const totalNet = totalIncome - totalExpense;

  const chartSeries = [
    { name: "Income", data: completedRows.map((r) => r.income.total) },
    { name: "Expense", data: completedRows.map((r) => r.expense.total) },
  ];

  const chartOptions = {
    chart: { type: "bar", toolbar: { show: false }, background: "transparent" },
    colors: ["#6aaa88", "#b87070"],
    plotOptions: { bar: { columnWidth: "55%", borderRadius: 3 } },
    dataLabels: { enabled: false },
    xaxis: {
      categories: completedRows.map((r) => shortFromKey(r.month_key)),
      labels: { style: { fontSize: "10px", colors: "#888" } },
      axisBorder: { show: false },
      axisTicks: { show: false },
    },
    yaxis: { show: false },
    grid: { show: false },
    legend: { show: false },
    tooltip: {
      shared: true,
      intersect: false,
      y: { formatter: (v) => `₹${fmt(v)}` },
    },
  };

  const active = activeIdx !== null ? ledger[activeIdx] : null;
  const incomplete = active ? isMonthIncomplete(active.month_key) : false;

  return (
    <React.Fragment>
      <style>{`
        .fre-scroll::-webkit-scrollbar { width: 4px; }
        .fre-scroll::-webkit-scrollbar-track { background: transparent; }
        .fre-scroll::-webkit-scrollbar-thumb { background: #ccc; border-radius: 4px; }
        .fre-modal-full .modal-dialog { max-width: 95vw !important; width: 95vw !important; }
        .fre-row-future td { color: #bbb !important; font-style: italic; background-color: #f9f9f9 !important; }
        .fre-row-done { cursor: pointer; }
        .fre-row-done:hover td { background-color: #f3eef8 !important; }
        .fre-pill { border-radius: 8px; padding: 12px 8px; flex: 1; text-align: center; box-shadow: 0 2px 6px rgba(0,0,0,0.09); }
      `}</style>

      {isLoading && <Loader />}

      <Card className="h-100 mb-0" style={{ overflow: "hidden" }}>
        <CardBody className="p-2 d-flex flex-column" style={{ height: "100%" }}>
          {/* ── Header ── */}
          <div className="d-flex align-items-center justify-content-between mb-2">
            <span className="text-purple fw-bold" style={{ fontSize: "14px" }}>
              Finance Report
            </span>
            <div className="d-flex align-items-center gap-2">
              <span
                className="d-flex align-items-center gap-1"
                style={{ fontSize: "11px", color: "#666" }}
              >
                <span
                  style={{
                    display: "inline-block",
                    width: 9,
                    height: 9,
                    borderRadius: 2,
                    backgroundColor: "#6aaa88",
                  }}
                />
                Inc
              </span>
              <span
                className="d-flex align-items-center gap-1"
                style={{ fontSize: "11px", color: "#666" }}
              >
                <span
                  style={{
                    display: "inline-block",
                    width: 9,
                    height: 9,
                    borderRadius: 2,
                    backgroundColor: "#b87070",
                  }}
                />
                Exp
              </span>
              <span
                className="badge"
                style={{
                  backgroundColor: "#ede8f5",
                  color: MOD_PURPLE,
                  fontSize: "10px",
                }}
              >
                {label}
              </span>
            </div>
          </div>

          {/* ── Stat pills ── */}
          <div className="d-flex gap-1 mb-2">
            <div className="fre-pill" style={{ backgroundColor: "#edf4f0" }}>
              <div style={{ fontSize: "13px", color: "#555" }}>
                <i
                  className="fas fa-arrow-up me-1"
                  style={{ color: "#4a8a68" }}
                />
                Income
              </div>
              <div
                className="fw-bold"
                style={{ fontSize: "14px", color: "#3a7055" }}
              >
                {fmtL(totalIncome)}
              </div>
            </div>
            <div className="fre-pill" style={{ backgroundColor: "#f4eded" }}>
              <div style={{ fontSize: "13px", color: "#555" }}>
                <i
                  className="fas fa-arrow-down me-1"
                  style={{ color: "#8a5050" }}
                />
                Expense
              </div>
              <div
                className="fw-bold"
                style={{ fontSize: "14px", color: "#7a3f3f" }}
              >
                {fmtL(totalExpense)}
              </div>
            </div>
            <div
              className="fre-pill"
              style={{ backgroundColor: totalNet >= 0 ? "#edf4f0" : "#f4eded" }}
            >
              <div style={{ fontSize: "13px", color: "#555" }}>
                <i
                  className="fas fa-balance-scale me-1"
                  style={{ color: totalNet >= 0 ? "#4a8a68" : "#8a5050" }}
                />
                Net P&L
              </div>
              <div
                className="fw-bold"
                style={{
                  fontSize: "14px",
                  color: totalNet >= 0 ? "#3a7055" : "#7a3f3f",
                }}
              >
                {totalNet < 0 ? "-" : "+"}
                {fmtL(Math.abs(totalNet))}
              </div>
            </div>
          </div>

          {/* ── Chart — only completed months ── */}
          {completedRows.length > 0 && (
            <div
              style={{
                marginBottom: 6,
                borderRadius: "10px",
                backgroundColor: "#fafafa",
                padding: "6px 2px 0",
                boxShadow: "0 2px 8px rgba(0,0,0,0.07)",
                border: "1px solid #eeebf3",
              }}
            >
              <ReactApexChart
                options={chartOptions}
                series={chartSeries}
                type="bar"
                height={110}
              />
            </div>
          )}

          {/* ── Monthly table ── */}
          <div
            className="fre-scroll flex-fill"
            style={{
              overflowY: "auto",
              scrollbarWidth: "thin",
              scrollbarColor: "#ccc transparent",
              minHeight: 0,
            }}
          >
            <table
              className="table table-sm table-bordered mb-0"
              style={{ tableLayout: "fixed", width: "100%", fontSize: "12px" }}
            >
              <colgroup>
                <col style={{ width: "18%" }} />
                <col style={{ width: "27%" }} />
                <col style={{ width: "27%" }} />
                <col style={{ width: "28%" }} />
              </colgroup>
              <thead
                style={{
                  position: "sticky",
                  top: 0,
                  zIndex: 2,
                  backgroundColor: "#f0eaf7",
                }}
              >
                <tr>
                  <th className="text-purple">Month</th>
                  <th className="text-end" style={{ color: "#3a7055" }}>
                    Income
                  </th>
                  <th className="text-end" style={{ color: "#7a3f3f" }}>
                    Expense
                  </th>
                  <th className="text-end" style={{ color: MOD_PURPLE }}>
                    Net
                  </th>
                </tr>
              </thead>
              <tbody>
                {ledger.map((row, i) => {
                  const inc = isMonthIncomplete(row.month_key);
                  const net = row.income.total - row.expense.total;
                  return (
                    <tr
                      key={i}
                      className={inc ? "fre-row-future" : "fre-row-done"}
                      onClick={() => !inc && setActiveIdx(i)}
                    >
                      <td
                        style={{
                          overflow: "hidden",
                          textOverflow: "ellipsis",
                          whiteSpace: "nowrap",
                        }}
                      >
                        {shortFromKey(row.month_key)}
                      </td>
                      <td
                        className="text-end"
                        style={{ color: inc ? undefined : "#3a7055" }}
                      >
                        {inc ? "—" : fmtL(row.income.total)}
                      </td>
                      <td
                        className="text-end"
                        style={{ color: inc ? undefined : "#7a3f3f" }}
                      >
                        {inc ? "—" : fmtL(row.expense.total)}
                      </td>
                      <td
                        className="text-end fw-semibold"
                        style={{
                          color: inc
                            ? undefined
                            : net >= 0
                            ? "#3a7055"
                            : "#7a3f3f",
                        }}
                      >
                        {inc
                          ? "—"
                          : (net < 0 ? "-" : "+") + fmtL(Math.abs(net))}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
              <tfoot
                style={{
                  position: "sticky",
                  bottom: 0,
                  zIndex: 2,
                  backgroundColor: "#f0eaf7",
                }}
              >
                <tr className="fw-bold">
                  <td className="text-purple">Total</td>
                  <td className="text-end" style={{ color: "#3a7055" }}>
                    {fmtL(totalIncome)}
                  </td>
                  <td className="text-end" style={{ color: "#7a3f3f" }}>
                    {fmtL(totalExpense)}
                  </td>
                  <td
                    className="text-end"
                    style={{ color: totalNet >= 0 ? "#3a7055" : "#7a3f3f" }}
                  >
                    {totalNet < 0 ? "-" : "+"}
                    {fmtL(Math.abs(totalNet))}
                  </td>
                </tr>
              </tfoot>
            </table>
          </div>
        </CardBody>
      </Card>

      {/* ── Month Detail Modal ── */}
      {active && (
        <Modal
          isOpen={activeIdx !== null}
          centered
          toggle={() => setActiveIdx(null)}
          size={xl}
        >
          <ModalHeader
            toggle={() => setActiveIdx(null)}
            style={{
              backgroundColor: "#ede8f5",
              borderBottom: "1px solid #cbb8e0",
            }}
          >
            <span className="fw-bold" style={{ color: MOD_PURPLE }}>
              {active.month}
            </span>
            <span
              className="ms-2 text-muted"
              style={{ fontSize: "13px", fontWeight: 400 }}
            >
              {label}
            </span>
          </ModalHeader>

          <ModalBody style={{ backgroundColor: "#faf8fc" }}>
            {/* Modal stat pills */}
            <div className="d-flex gap-2 mb-3">
              <div
                className="flex-fill text-center p-2 rounded"
                style={{ backgroundColor: "#edf4f0" }}
              >
                <div style={{ fontSize: "13px", color: "#555" }}>
                  <i
                    className="fas fa-arrow-up me-1"
                    style={{ color: "#4a8a68" }}
                  />
                  Income
                </div>
                <div
                  className="fw-bold"
                  style={{ fontSize: "16px", color: "#3a7055" }}
                >
                  ₹{fmt(active.income.total)}
                </div>
              </div>
              <div
                className="flex-fill text-center p-2 rounded"
                style={{ backgroundColor: "#f4eded" }}
              >
                <div style={{ fontSize: "13px", color: "#555" }}>
                  <i
                    className="fas fa-arrow-down me-1"
                    style={{ color: "#8a5050" }}
                  />
                  Expense
                </div>
                <div
                  className="fw-bold"
                  style={{ fontSize: "16px", color: "#7a3f3f" }}
                >
                  ₹{fmt(active.expense.total)}
                </div>
              </div>
              <div
                className="flex-fill text-center p-2 rounded"
                style={{
                  backgroundColor: active.net >= 0 ? "#edf4f0" : "#f4eded",
                }}
              >
                <div style={{ fontSize: "13px", color: "#555" }}>
                  <i
                    className="fas fa-balance-scale me-1"
                    style={{ color: active.net >= 0 ? "#4a8a68" : "#8a5050" }}
                  />
                  Net
                </div>
                <div
                  className="fw-bold"
                  style={{
                    fontSize: "16px",
                    color: active.net >= 0 ? "#3a7055" : "#7a3f3f",
                  }}
                >
                  {active.net < 0 ? "-" : "+"}₹{fmt(Math.abs(active.net))}
                </div>
              </div>
              <div
                className="flex-fill text-center p-2 rounded"
                style={{ backgroundColor: "#ede8f5" }}
              >
                <div style={{ fontSize: "13px", color: "#555" }}>
                  <i
                    className="fas fa-wallet me-1"
                    style={{ color: MOD_PURPLE }}
                  />
                  Closing
                </div>
                <div
                  className="fw-bold"
                  style={{ fontSize: "16px", color: MOD_PURPLE }}
                >
                  {incomplete ? "—" : `₹${fmt(active.closing)}`}
                </div>
              </div>
            </div>

            <div className="row g-3">
              {/* Income table */}
              <div className="col-md-6">
                <p
                  className="fw-semibold mb-2"
                  style={{ color: "#3a7055", fontSize: "14px" }}
                >
                  <i className="fas fa-arrow-up me-1" />
                  Income
                </p>
                <div
                  className="fre-scroll responsive-table"
                  style={{
                    maxHeight: "340px",
                    overflowY: "auto",
                    scrollbarWidth: "thin",
                    scrollbarColor: "#ccc transparent",
                  }}
                >
                  <table className="table table-sm table-bordered mb-0">
                    <thead
                      style={{
                        position: "sticky",
                        top: 0,
                        zIndex: 2,
                        backgroundColor: "#f0eaf7",
                      }}
                    >
                      <tr>
                        <th className="header">#</th>
                        <th className="header">Invoice</th>
                        <th className="header">Customer</th>
                        <th className="header text-end">Amount</th>
                        <th className="header">Date</th>
                      </tr>
                    </thead>
                    <tbody>
                      {/* B/F row */}
                      <tr style={{ backgroundColor: "#ede8f5" }}>
                        <td className="text-muted">—</td>
                        <td
                          colSpan={2}
                          className="fw-semibold"
                          style={{ color: MOD_PURPLE }}
                        >
                          Balance B/F
                        </td>
                        <td
                          className="text-end fw-semibold"
                          style={{ color: MOD_PURPLE }}
                        >
                          {activeIdx === 0 ? "—" : `₹${fmt(active.bf)}`}
                        </td>
                        <td className="text-muted">—</td>
                      </tr>
                      {active.income.items.length === 0 ? (
                        <tr>
                          <td
                            colSpan={5}
                            className="text-center text-muted py-3"
                          >
                            No income entries
                          </td>
                        </tr>
                      ) : (
                        active.income.items.map((it, j) => (
                          <tr key={j}>
                            <td>{j + 1}</td>
                            <td>{it.invoice_number || "—"}</td>
                            <td>{it.customer_name || "—"}</td>
                            <td
                              className="text-end"
                              style={{ color: "#3a7055" }}
                            >
                              ₹{fmt(it.amount)}
                            </td>
                            <td>{it.date || "—"}</td>
                          </tr>
                        ))
                      )}
                    </tbody>
                    <tfoot
                      style={{
                        position: "sticky",
                        bottom: 0,
                        zIndex: 2,
                        backgroundColor: "#f0eaf7",
                      }}
                    >
                      <tr className="fw-bold">
                        <td colSpan={3}>Total</td>
                        <td className="text-end" style={{ color: "#3a7055" }}>
                          ₹{fmt(active.income.total)}
                        </td>
                        <td />
                      </tr>
                    </tfoot>
                  </table>
                </div>
              </div>

              {/* Expense table */}
              <div className="col-md-6">
                <p
                  className="fw-semibold mb-2"
                  style={{ color: "#7a3f3f", fontSize: "14px" }}
                >
                  <i className="fas fa-arrow-down me-1" />
                  Expense
                </p>
                <div
                  className="fre-scroll responsive-table"
                  style={{
                    maxHeight: "340px",
                    overflowY: "auto",
                    scrollbarWidth: "thin",
                    scrollbarColor: "#ccc transparent",
                  }}
                >
                  <table className="table table-sm table-bordered mb-0">
                    <thead
                      style={{
                        position: "sticky",
                        top: 0,
                        zIndex: 2,
                        backgroundColor: "#f0eaf7",
                      }}
                    >
                      <tr>
                        <th className="header">#</th>
                        <th className="header">Title</th>
                        <th className="header">Type</th>
                        <th className="header text-end">Amount</th>
                        <th className="header">Date</th>
                      </tr>
                    </thead>
                    <tbody>
                      {active.expense.items.length === 0 ? (
                        <tr>
                          <td
                            colSpan={5}
                            className="text-center text-muted py-3"
                          >
                            No expense entries
                          </td>
                        </tr>
                      ) : (
                        active.expense.items.map((it, j) => (
                          <tr key={j}>
                            <td>{j + 1}</td>
                            <td>{it.desc}</td>
                            <td>{typeBadge(it.type)}</td>
                            <td
                              className="text-end"
                              style={{ color: "#7a3f3f" }}
                            >
                              ₹{fmt(it.amount)}
                            </td>
                            <td>{it.date || "—"}</td>
                          </tr>
                        ))
                      )}
                    </tbody>
                    <tfoot
                      style={{
                        position: "sticky",
                        bottom: 0,
                        zIndex: 2,
                        backgroundColor: "#f0eaf7",
                      }}
                    >
                      <tr className="fw-bold">
                        <td colSpan={3}>Total</td>
                        <td className="text-end" style={{ color: "#7a3f3f" }}>
                          ₹{fmt(active.expense.total)}
                        </td>
                        <td />
                      </tr>
                    </tfoot>
                  </table>
                </div>
              </div>
            </div>
          </ModalBody>
        </Modal>
      )}
    </React.Fragment>
  );
};

export default FinanceReportElement;


